/**
 * Site Variance detail route (`/:orgId/neat/variance`).
 *
 * Reached from the WATCH "Site variance" tile. Ranked outlier table: sites
 * deviating from BOTH their own weather-normalized (TOWT) baseline AND their
 * peer cohort, worst first; each row expands to its anomalous bill cycles.
 *
 * Payload-driven (renders from one `VarianceDetailPayload`), live-only via
 * `useGetVariance` (GET /neat/insights/variance). On error or before load
 * resolves, no fixture fallback — shows an error state.
 * Historical framing throughout — observed deviation, never a forecast.
 */
import type React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Page, PageCard } from "@/components"
import { Button } from "@/nzds/inputs/button"

import { ArrowBackRounded, DownloadRounded } from "@mui/icons-material"
import { Box, CircularProgress, Stack, Typography } from "@mui/material"

import { useGetVariance } from "../../data/use-get-variance"
import { useInsightsNavigate } from "../../data/use-insights-navigate"
import { Eyebrow, SectionTitle } from "../../homepage/atoms"
import { WindowSelector } from "../../homepage/window-selector"
import { VirtualSubmeteringHeader } from "../../virtual-submetering-header"
import { SENSITIVITY_DEFAULT, SensitivityCog } from "./sensitivity-cog"
import { downloadVarianceRanking } from "./variance-export"
import { VarianceTable } from "./variance-table"

export const VarianceRoute: React.FC = () => {
  const navigate = useInsightsNavigate()
  const { t } = useTranslation("common")
  // Undefined until the user actually moves the cog, so the default request
  // carries no `t_peer` at all. The BFF caches the enriched detail under the
  // forwarded query and the homepage tile reads that same entry; the homepage
  // never sends `t_peer`, so sending the server default here would key the two
  // apart and opening this page could never warm the tile.
  const [tPeer, setTPeer] = useState<number | undefined>(undefined)

  // The display window is shared with the rest of /neat (URL-backed), so a
  // selection made on the homepage lands here already applied. Only `t_peer`
  // is page-local.
  const {
    siteVarianceData: payload,
    siteVarianceIsPending: isPending,
    siteVarianceIsFetching: isFetching,
  } = useGetVariance(tPeer)
  // Background refetch (window/sensitivity change) while prior rows stay on
  // screen via keepPreviousData — surface it so the table doesn't look frozen.
  const refreshing = isFetching && !isPending

  if (isPending) {
    return (
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "center", py: 6, justifyContent: "center" }}
      >
        <CircularProgress size={20} />
        <Typography color="text.secondary" variant="body2">
          Loading site variance…
        </Typography>
      </Stack>
    )
  }

  if (!payload) {
    return (
      <Typography color="error" sx={{ p: 3 }} variant="body1">
        Couldn&apos;t load site variance. Please try again.
      </Typography>
    )
  }

  return (
    <>
      <VirtualSubmeteringHeader />
      <Page>
        <Stack spacing={3}>
          <Box>
            <Button
              onClick={() => {
                navigate("..")
              }}
              size="small"
              startIcon={<ArrowBackRounded />}
              sx={{ mb: 1 }}
              variant="text"
            >
              Back to Insights
            </Button>

            <Box sx={{ mb: 2 }}>
              <WindowSelector />
            </Box>

            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
            >
              <Box>
                <Eyebrow>
                  WATCH · SITE VARIANCE · {payload.period_label.toUpperCase()}
                </Eyebrow>
                <SectionTitle>Site variance</SectionTitle>
                <Typography
                  color="text.secondary"
                  sx={{ mt: 0.5, maxWidth: 640 }}
                  variant="body2"
                >
                  Sites deviating from both their own weather-normalized
                  baseline and their peer cohort.
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center" }}
              >
                {refreshing ? (
                  <Stack
                    aria-live="polite"
                    direction="row"
                    spacing={0.75}
                    sx={{ alignItems: "center", mr: 0.5 }}
                  >
                    <CircularProgress size={14} />
                    <Typography color="text.secondary" variant="dataLabel">
                      Updating…
                    </Typography>
                  </Stack>
                ) : null}
                <SensitivityCog
                  onChange={setTPeer}
                  value={tPeer ?? SENSITIVITY_DEFAULT}
                />
                <Button
                  // Disabled while a window/sensitivity refetch is in flight:
                  // the payload on screen still belongs to the old controls.
                  disabled={refreshing}
                  endIcon={<DownloadRounded />}
                  onClick={() => {
                    downloadVarianceRanking(payload, t)
                  }}
                  size="small"
                  variant="text"
                >
                  {t("neat.variance.exportRanking")}
                </Button>
              </Stack>
            </Stack>
          </Box>

          <PageCard
            aria-busy={refreshing}
            sx={{
              p: 0,
              overflow: "hidden",
              opacity: refreshing ? 0.55 : 1,
              pointerEvents: refreshing ? "none" : "auto",
              transition: "opacity 0.15s ease",
            }}
          >
            <VarianceTable sites={payload.sites} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 2.5,
                py: 1.75,
                bgcolor: (theme) => theme.palette.grey[25],
                borderTop: (theme) => `1px solid ${theme.palette.grey[100]}`,
              }}
            >
              <Typography color="text.secondary" variant="dataLabel">
                {payload.off_baseline_count} of {payload.total_sites} sites off
                baseline · {payload.baseline_note}
              </Typography>
              <Button
                disabled={refreshing}
                endIcon={<DownloadRounded />}
                onClick={() => {
                  downloadVarianceRanking(payload, t)
                }}
                size="small"
                variant="text"
              >
                {t("neat.variance.exportRankingCsv")}
              </Button>
            </Box>
          </PageCard>

          <Typography color="text.secondary" variant="caption">
            {t("neat.variance.methodology")}
          </Typography>
        </Stack>
      </Page>
    </>
  )
}
